import React from 'react';
import type { AlertAnchor, GlobalAlert } from '../../api/globalAlerts.types';
import CustomAlert from '../CustomAlert/CustomAlert';
import Link from '../Link/Link';

type RenderAlertWithAnchorsProps = {
  alert: GlobalAlert;
  baseUrl?: string;
  onDismiss: (alert: GlobalAlert) => void;
};

const PARTS_REGEX = /(\{anchor\}|\*\*[^*]+\*\*)/g;
const ABSOLUTE_OR_PROTOCOL_RELATIVE_URL_REGEX = /^(?:[a-z][a-z\d+.-]*:|\/\/)/i;

const renderParts = (
  parts: string[],
  anchors: AlertAnchor[] | undefined,
  baseUrl: string | undefined,
  anchorIdx: { current: number }
): React.ReactNode[] => {
  return parts.map((part, idx) => {
    if (part === '{anchor}') {
      if (!anchors || anchorIdx.current >= anchors.length) {
        return <React.Fragment key={`t-${idx}`} />;
      }

      const anchor = anchors[anchorIdx.current++];
      if (!anchor) {
        return <React.Fragment key={`t-${idx}`} />;
      }

      const shouldPrefixBaseUrl = !anchor?.email && !ABSOLUTE_OR_PROTOCOL_RELATIVE_URL_REGEX.test(anchor.href);

      return (
        <Link
          key={`t-${idx}`}
          href={`${shouldPrefixBaseUrl ? (baseUrl ?? '') : ''}${anchor.href}`}
          external={anchor?.external}
          aria={{ label: anchor.title }}
        >
          {anchor.title}
        </Link>
      );
    }

    if (part.startsWith('**') && part.endsWith('**')) {
      const inner = part.slice(2, -2);
      if (!inner.trim()) {
        console.warn('GlobalAlerts: malformed bold segment:', part);
        return <React.Fragment key={`t-${idx}`}>{part}</React.Fragment>;
      }
      return <strong key={`t-${idx}`}>{inner}</strong>;
    }

    return <React.Fragment key={`t-${idx}`}>{part}</React.Fragment>;
  });
};

export const RenderAlertWithAnchors: React.FC<RenderAlertWithAnchorsProps> = ({ alert, baseUrl, onDismiss }) => {
  const parts = alert.message_content.split(PARTS_REGEX);
  const anchorIdx = { current: 0 };

  return (
    <CustomAlert
      status={alert.alert_type}
      global={true}
      dismissible={alert?.dismissible}
      instantlyDismiss={false}
      onDismiss={() => onDismiss(alert)}
    >
      {renderParts(parts, alert.anchors, baseUrl, anchorIdx)}
    </CustomAlert>
  );
};
